import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';

import AIChatbot from '../../components/ui/AIChatbot';
import { useAuth } from '../../contexts/AuthContext';
import problemStatementService from '../../services/problemStatementService';


// Import PCF Admin components for reuse
import MessagesTab from '../pcf-admin-dashboard/components/MessagesTab';
import NotificationTab from '../pcf-admin-dashboard/components/NotificationTab';
import TeamManagementTab from '../pcf-admin-dashboard/components/TeamManagementTab';
import MeetingsTab from '../pcf-admin-dashboard/components/MeetingsTab';

// Import existing components for Overview tab
import MetricsCard from './components/MetricsCard';
import ProblemsTable from './components/ProblemsTable';
import ActivityFeed from './components/ActivityFeed';
import UpcomingMeetings from './components/UpcomingMeetings';
import SubmitProblemModal from './components/SubmitProblemModal';

const CityOfficialDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(null);
  const { user, userProfile } = useAuth();
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [refreshProblems, setRefreshProblems] = useState(0);
  const [metrics, setMetrics] = useState([
    {
      title: 'Active Problems',
      value: '0',
      change: '',
      changeType: 'increase',
      icon: 'FileText',
      color: 'primary'
    },
    {
      title: 'Assigned Teams',
      value: '0',
      change: '',
      changeType: 'increase',
      icon: 'Users',
      color: 'success'
    },
    {
      title: 'Pending Submissions',
      value: '0',
      change: '',
      changeType: 'decrease',
      icon: 'Clock',
      color: 'warning'
    }
  ]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  // Set current user from auth context
  useEffect(() => {
    if (userProfile) {
      setCurrentUser({
        id: userProfile?.id,
        name: userProfile?.full_name,
        role: 'city-official',
        city: userProfile?.city || 'Unknown',
        department: userProfile?.organization || 'Urban Development'
      });
    }
  }, [userProfile]);

  // Fetch metrics data
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!user?.id) return;

      setIsLoadingMetrics(true);
      try {
        // Fetch all problem statements for the current user
        const problemStatements = await problemStatementService?.getUserProblemStatements(user?.id);

        // Count Active Problems (status = Approved)
        const activeProblems = problemStatements?.filter(ps => ps?.status === 'approved')?.length || 0;

        // Count Pending Submissions (status = Pending)
        const pendingSubmissions = problemStatements?.filter(ps => ps?.status === 'pending')?.length || 0;

        // FIXED: Count unique teams assigned to APPROVED problem statements
        const approvedProblems = problemStatements?.filter(ps => ps?.status === 'approved' && ps?.assignedTeamId);
        const uniqueTeamIds = new Set(approvedProblems?.map(ps => ps?.assignedTeamId)?.filter(Boolean));
        const assignedTeams = uniqueTeamIds?.size || 0;

        // Update metrics state
        setMetrics([
          {
            title: 'Active Problems',
            value: activeProblems?.toString(),
            change: '',
            changeType: 'increase',
            icon: 'FileText',
            color: 'primary'
          },
          {
            title: 'Assigned Teams',
            value: assignedTeams?.toString(),
            change: '',
            changeType: 'increase',
            icon: 'Users',
            color: 'success'
          },
          {
            title: 'Pending Submissions',
            value: pendingSubmissions?.toString(),
            change: '',
            changeType: 'decrease',
            icon: 'Clock',
            color: 'warning'
          }
        ]);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [user?.id, refreshProblems]);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/city-official-dashboard' }
  ];

  // Updated tabs to match requirements
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'meetings', label: 'Meetings', icon: 'Calendar' },
    { id: 'teams', label: 'Teams', icon: 'Users' }
  ];

  // Create custom menu for Header component
  const customHeaderMenu = tabs?.map(tab => ({
    id: tab?.id,
    label: tab?.label,
    icon: tab?.icon,
    onClick: () => setActiveTab(tab?.id),
    active: activeTab === tab?.id
  }));

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Metrics Overview */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {metrics?.map((metric, index) => (
          <MetricsCard key={index} {...metric} isLoading={isLoadingMetrics} />
        ))}
      </div>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Problems Table */}
          <div className="bg-card border border-border rounded-lg shadow-soft">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-foreground">My Problem Statements</h3>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowSubmitModal(true)}
                  iconName="Plus"
                  iconPosition="left"
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white"
                >
                  Submit New
                </Button>
              </div>
              <ProblemsTable key={refreshProblems} />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          <UpcomingMeetings />
          <ActivityFeed />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'messages':
        return <MessagesTab messages={[]} onSendMessage={() => {}} teamMembers={[]} currentUser={user} />;
      case 'notifications':
        return <NotificationTab />;
      case 'meetings':
        return <MeetingsTab />;
      case 'teams':
        return <TeamManagementTab filterByCityOfficial={currentUser?.id} />;
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole="city-official" 
        userName={currentUser?.name || 'City Official'}
        notifications={3}
        customMenu={customHeaderMenu}
      />
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-6 max-w-7xl">
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                City Official Dashboard
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {currentUser?.city} • {currentUser?.department}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="mb-4 sm:mb-6 lg:hidden">
          <div className="bg-card border border-border rounded-lg p-2">
            <div className="grid grid-cols-2 gap-2">
              {tabs?.map((tab) => (
                <Button
                  key={tab?.id}
                  variant={activeTab === tab?.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab(tab?.id)}
                  iconName={tab?.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="w-full justify-start text-xs sm:text-sm"
                >
                  {tab?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg shadow-soft">
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
      
      {/* AI Chatbot */}
      <AIChatbot userRole="city_official" userId={user?.id} />

      {/* Submit Problem Modal */}
      <SubmitProblemModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSuccess={() => {
          setRefreshProblems(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default CityOfficialDashboard;